import React from "react";
import { Grid, IconButton, Stack } from "@mui/material";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import styled from "styled-components";
import BadgeAchieventProfile from "src/components/badge-achievent-profile";
import SummarySettingsModal from "src/scenes/summary/summary-settings-modal";
import request from "src/utils/request";
import { useAuth } from "src/components/providers/auth-context";
import { format, subHours } from "date-fns";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { Helmet } from "react-helmet";
import Portal from "src/components/portal";
import DemoBarPersonal from "src/components/demo-bar-personal";
import DemoBarDemo from "src/components/demo-bar-demo";
import { ActiveIntegrationsProvider } from "src/scenes/dashboard/active-integrations-provider";
import calculateLastWeekDifference from "src/scenes/summary/summary-calculate-last-week-diff";

const SectionTitle = styled.h2`
    font-size: 16px;
`;

const MainValue = styled.p`
    font-size: 60px;
    margin: 0;
    line-height: 1;
`;

const MainTitle = styled.p`
    font-size: 13px;
    margin: 0 0 3px 0;
    line-height: 1;
`;

const SubTitle = styled.p`
    font-size: 11px;
    font-weight: 200;
    margin: 0 0 26px 0;
    line-height: 1;
`;

const MissionsStack = styled(Stack)`
    border: 1px solid #414141;
    border-radius: 8px;
    padding: 12px;
    margin: 8px;
    width: 257px;
`;

const StartIcon = styled.img`
    width: 17px;
    margin-right: 12px;
`;

const WeeklySummary = () => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [summaryData, setSummaryData] = React.useState([]);
    const [dataLoading, setIsDataLoading] = React.useState(true);
    const { isDemo, isUserPayingCustomer } = useAuth();

    const getLatestSummary = async () => {
        setIsDataLoading(true);
        try {
            let response;
            if (isDemo || !isUserPayingCustomer) {
                response = (await import("src/scenes/summary/summary-mock-data")).default;
            } else {
                response = await request(`/api/users/me/summary`, {
                    method: "GET",
                });
            }
            setSummaryData(response);
            setIsDataLoading(false);
        } catch (error) {
            setIsDataLoading(false);
        }
        return null;
    };

    React.useEffect(() => {
        getLatestSummary();
    }, []);

    const isProd = process.env.ENVIRONMENT_NAME === "prod";
    const setAssetLink = () => {
        if (!isProd) return "https://assets-sit.adadot.com/missions";
        return "https://assets.adadot.com/missions";
    };
    const assetLink = setAssetLink();

    const whatType = (metric) => {
        if (metric.type === "number") return null;
        if (metric.type === "percentile") return "%";
        if (metric.type === "number") return "hrs";
        return null;
    };

    if (dataLoading && summaryData && summaryData.length === 0) return null;
    if (!dataLoading && summaryData && summaryData.length === 0)
        return <NoDataPlaceholder variant="no-summary" />;
    return (
        <ActiveIntegrationsProvider>
            <Helmet>
                <title>Adadot - Weekly Summary</title>
            </Helmet>
            <Portal>
                <DemoBarPersonal />
                <DemoBarDemo />
            </Portal>
            <Stack direction="column">
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <SectionTitle>Weekly summary</SectionTitle>
                        <MainTitle>
                            for {format(new Date(summaryData.fromDate), "d MMM y")} -{" "}
                            {format(subHours(new Date(summaryData.toDate), 2), "d MMM y")}
                        </MainTitle>
                    </Stack>
                    <IconButton aria-label="summary settings">
                        <SettingsApplicationsIcon onClick={() => setModalOpen(true)} />
                    </IconButton>
                </Stack>
                <Grid container mt={2}>
                    <Grid item md={8}>
                        <Grid container spacing={2}>
                            {summaryData.stats.map((metric) => {
                                return (
                                    <Grid item md={4}>
                                        <MainValue>
                                            {metric.value}
                                            {whatType(metric)}
                                        </MainValue>
                                        <MainTitle>{metric.name}</MainTitle>
                                        {calculateLastWeekDifference(metric)}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>
                    <Grid item md={3}>
                        {summaryData &&
                        summaryData.missions.length === 0 &&
                        summaryData.achievements.length === 0 ? (
                            <NoDataPlaceholder variant="summary" />
                        ) : (
                            <Stack direction="column">
                                <Stack direction="column">
                                    <MainTitle>Goals Reached</MainTitle>
                                    {summaryData && summaryData.missions.length > 0 ? (
                                        <Grid
                                            container
                                            style={{
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                                maxHeight: "280px",
                                            }}
                                            mb={2}
                                            direction="row"
                                        >
                                            {summaryData.missions.map((mission) => (
                                                <Grid item key={mission.missionId} direction="row">
                                                    <MissionsStack direction="row" mr={1}>
                                                        <StartIcon
                                                            src={`${assetLink}/${mission.missionCategory}/${mission.missionId}.svg`}
                                                            alt="image"
                                                        />
                                                        <Stack direction="column">
                                                            <MainTitle>{mission.title}</MainTitle>
                                                            <SubTitle style={{ marginBottom: 0 }}>
                                                                Completed on{" "}
                                                                {format(
                                                                    new Date(mission.completedOn),
                                                                    "eeee P"
                                                                )}
                                                            </SubTitle>
                                                        </Stack>
                                                    </MissionsStack>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <NoDataPlaceholder variant="summary-missions" />
                                    )}
                                </Stack>
                                <Stack direction="column">
                                    <MainTitle>Latest Achievements</MainTitle>
                                    {summaryData && summaryData.achievements.length > 0 ? (
                                        <Grid container direction="row">
                                            {summaryData.achievements.map((achievement) => (
                                                <BadgeAchieventProfile achievement={achievement} />
                                            ))}
                                        </Grid>
                                    ) : (
                                        <NoDataPlaceholder variant="summary-achievements" />
                                    )}
                                </Stack>
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </Stack>
            <SummarySettingsModal
                open={modalOpen}
                handleClose={() => setModalOpen(!modalOpen)}
                getLatestSummary={getLatestSummary}
            />
        </ActiveIntegrationsProvider>
    );
};

export default WeeklySummary;
